import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import { StatusCode } from "api/protocols";
import { DeleteLineItemToCart, PutLineItemToCart } from "api/rpc/2024-04/facilityAdmin/order/cart/lineItem";
import { IDiscount } from "api/rpc/2022-09/facilityAdmin/product/discount";
import { IUserPermissions } from "redux/reducers/models/user";
import { loadCart, loadTableCart } from "redux/actions/cart";
import { showError, showSuccess } from "redux/actions/ui";
import { ICart, ICartLineItem, ICartTaxLine } from "redux/reducers/models/cart";
import { IProduct, IVariant, TComboVariants } from "redux/reducers/models/product";

import { isEmpty, isNumeric } from "helpers/Helpers";
import {
  canRemoveCartLineItem,
  canSetCartLineItemQuantity,
  minCartLineItemQuantity,
} from "helpers/cartLineItemPermissions";

import Sheet from "components/sheet/Sheet";
import Input from "components/form/input/Input";
import { ButtonNew as Button } from "components/buttonNew/index";
import DiscountLineItemModal from "components/discountLineItemModal/DiscountLineItemModal";
import ReactDOM from "react-dom";
import { useRegisterContext } from "./RegisterContext";
import { PostDiscountLine } from "api/rpc/2024-04/facilityAdmin/order/cart/discountLine";
import "elements/register/register.scss";
import { useAppSelector } from "hooks/redux";
import { isEqual } from "lodash";
import useCurrencyFacility from "hooks/useCurrency/useCurrencyFacility";
import Toggle from "components/form/toggle/Toggle";
import { GetVariant } from "api/rpc/2024-04/facilityAdmin/product/variant";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronRight, faChevronUp, faPlus } from "@fortawesome/pro-regular-svg-icons";
import { Badge } from "components/badge/Badge";

export interface ILineItem {
  cart_id: number;
  cost: number;
  custom: boolean;
  fulfillment_required: string | null;
  gift_card: boolean;
  id: number;
  kitchen_chit_id: number | null;
  non_refundable: boolean;
  note: string | null;
  parent_id: number | null;
  pre_paid_required: number;
  preferred_title: string | null;
  price: number;
  product: IProduct;
  product_default_image?: string | null;
  product_id: number;
  product_title: string;
  quantity: number;
  requires_shipping: boolean | null;
  subtitle: string | null;
  subtotal_price: number;
  tax_included: boolean;
  tax_lines: ICartTaxLine[];
  taxable: boolean;
  total_discount: number;
  total_price: number;
  total_tax: number;
  variant_id: number;
  variant_title: string;
}

export interface IUpdateLineItemModalState {
  lineItem: ICartLineItem;
  inputQuantity: string;
  inputPrice: string;
  inputComboPrices: { price: string; variant_id: number }[];
  inputNote: string;
  isModalVisible: boolean;
  taxExemptionApplied: boolean;
}

interface ILineItemModalBase {
  lineItemState: IUpdateLineItemModalState;
  setLineItemState: React.Dispatch<React.SetStateAction<IUpdateLineItemModalState>>;
  openModifiersSheet?: (lineItem: ICartLineItem) => void;
  openDynamicComboSheet?: (lineItem: ICartLineItem) => void;
  permissions: IUserPermissions;
  discounts: IDiscount[];
  cart: ICart | Partial<ICart>;
}

interface ILineItemStateData extends ILineItemModalBase {
  dataOrigin: "state";
  modalActions: ILineItemModalActions;
}

interface ILineItemReduxData extends ILineItemModalBase {
  dataOrigin: "redux";
  modalActions: Partial<ILineItemModalActions>;
}

interface ILineItemModalActions {
  onOk: (updatedCart: ICart) => void;
  updateTabCart?: (updatedCart: ICart) => void;
}

type LineItemModalType = ILineItemStateData | ILineItemReduxData;

export default function LineItemModal(props: LineItemModalType) {
  const {
    updateRegisterState,
    addLineItemUpdating,
    removeLineItemUpdating,
    participantPaidFees,
    updateParticipantPaidFees,
  } = useRegisterContext();
  const { teeSheetStore } = useAppSelector(store => store);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { getCurrencySymbol } = useCurrencyFacility();
  const {
    lineItemState,
    setLineItemState,
    permissions,
    discounts,
    dataOrigin,
    modalActions,
    openModifiersSheet,
    openDynamicComboSheet,
    cart,
  } = props;

  const [discountModal, setDiscountModal] = useState({
    isOpen: false,
    selectedDiscountId: -1,
    selectedDiscount: null,
    noteDiscount: "",
  });

  const [isPriceAfterTax, setIsPriceAfterTax] = useState(false);
  const [isNoteVisible, setIsNoteVisible] = useState(false);
  const [appliedDiscountLabel, setAppliedDiscountLabel] = useState<string | null>(null);
  const [isIncludedProductsExpanded, setIsIncludedProductsExpanded] = useState(true);
  const [isModifiersExpanded, setIsModifiersExpanded] = useState(true);

  const [variant, setVariant] = useState<IVariant>();

  const isDynamicCombo =
    lineItemState?.lineItem?.product?.type === "Combo" &&
    (lineItemState?.lineItem?.product?.dynamic_combo === true ||
      (lineItemState?.lineItem?.dynamic_combo_selections?.length ?? 0) > 0);
  const isRegularCombo = lineItemState?.lineItem?.product?.type === "Combo" && !isDynamicCombo;
  const dynamicComboSelections = lineItemState?.lineItem?.dynamic_combo_selections ?? [];
  const hasModifiers =
    !!(lineItemState.lineItem.product?.meta?.has_modifiers || lineItemState.lineItem.product?.has_modifiers) &&
    lineItemState.lineItem?.parent_id === null;
  const modifierLineItems =
    cart?.line_items?.filter(cartLineItem => cartLineItem.parent_id === lineItemState.lineItem?.id) ?? [];

  useEffect(() => {
    if (isRegularCombo) {
      void loadComboVariants();
    }
  }, []);

  async function loadComboVariants() {
    const resp = await GetVariant({ id: lineItemState.lineItem.variant_id, combination_variants: true }, true);
    if (resp.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading combo variants")));
      return;
    }
    const variant = resp.data[0];
    setVariant(variant);
    setLineItemState({
      ...lineItemState,
      inputComboPrices: lineItemState.lineItem.combo_selections?.map(cs => ({
        variant_id: cs.variant_id ?? 0,
        price: (Number(cs.quantity) * Number(cs.price)).toFixed(2),
      })),
    });
  }

  const originalQuantity = lineItemState?.lineItem?.quantity ?? 1;
  const minimumQuantity = minCartLineItemQuantity(permissions, lineItemState?.lineItem);

  const lineItemDiscount = cart?.discount_lines?.find(
    discount => discount.cart_line_item_id === lineItemState.lineItem?.id,
  );
  const discountBadgeText =
    appliedDiscountLabel ??
    (lineItemDiscount
      ? lineItemDiscount.custom
        ? t("Custom discount applied")
        : t("{{title}} applied", { title: lineItemDiscount.title })
      : null);

  const handlePriceAfterTaxToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setIsPriceAfterTax(checked);

    const comboPrices = lineItemState.inputComboPrices.map(i => {
      const comboTaxRate =
        variant?.combination_variants
          .find(cv => cv.id === i.variant_id)
          ?.inventory_item.inventory_level.tax_lines?.reduce((sum, tax_line) => sum + tax_line.tax, 0) ?? 0;
      i.price = checked
        ? (parseFloat(i.price) * (1 + comboTaxRate)).toFixed(2)
        : (parseFloat(i.price) / (1 + comboTaxRate)).toFixed(2);
      return i;
    });

    setLineItemState({ ...lineItemState, inputComboPrices: comboPrices });

    const taxRate =
      (lineItemState.lineItem?.tax_lines as unknown as ICartTaxLine[])?.reduce((sum, tax) => sum + tax.rate, 0) || 0;

    if (isNumeric(lineItemState.inputPrice)) {
      const currentVal = parseFloat(lineItemState.inputPrice);
      if (checked) {
        setLineItemState({ ...lineItemState, inputPrice: (currentVal * (1 + taxRate)).toFixed(2) });
      } else {
        setLineItemState({ ...lineItemState, inputPrice: (currentVal / (1 + taxRate)).toFixed(2) });
      }
    }
  };

  const openDiscountModal = () => {
    setLineItemState(prevState => ({ ...prevState, isModalVisible: false }));
    setDiscountModal({
      ...discountModal,
      isOpen: true,
      selectedDiscountId: -1,
      selectedDiscount: null,
      noteDiscount: "",
    });
  };

  const closeDiscountModal = (returnToUpdate = true) => {
    setDiscountModal({
      ...discountModal,
      isOpen: false,
      selectedDiscountId: -1,
      selectedDiscount: null,
      noteDiscount: "",
    });

    if (returnToUpdate) {
      setLineItemState(prevState => {
        if (!prevState.lineItem) {
          return prevState;
        }
        return { ...prevState, isModalVisible: true };
      });
    }
  };

  const resetInputState = () => {
    ReactDOM.unstable_batchedUpdates(() => {
      updateRegisterState({ modifierLineItem: null });
      setIsNoteVisible(false);
      setAppliedDiscountLabel(null);
      setIsIncludedProductsExpanded(true);
      setLineItemState({
        ...lineItemState,
        lineItem: null,
        isModalVisible: false,
        inputQuantity: "",
        inputPrice: "",
        inputNote: "",
      });
    });
  };

  // Update the line item
  async function handleUpdateLineItem(discountApplied = false) {
    const quantity = parseInt(lineItemState.inputQuantity);

    if (!canSetCartLineItemQuantity(permissions, quantity, lineItemState?.lineItem)) {
      dispatch(showError(t("You do not have permission to remove items from the cart.")));
      return;
    }

    const taxRate =
      (lineItemState.lineItem?.tax_lines as unknown as ICartTaxLine[])?.reduce((sum, tax) => sum + tax.rate, 0) || 0;
    const rawPrice = isPriceAfterTax
      ? parseFloat(lineItemState.inputPrice) / (1 + taxRate)
      : parseFloat(lineItemState.inputPrice);

    const price = parseFloat(rawPrice.toFixed(2));
    const comboPrices = lineItemState.inputComboPrices.map(i => {
      const comboTaxRate =
        variant?.combination_variants
          .find(cv => cv.id === i.variant_id)
          ?.inventory_item.inventory_level.tax_lines?.reduce((sum, tax_line) => sum + tax_line.tax, 0) ?? 0;
      const comboPrice = isPriceAfterTax ? parseFloat(i.price) / (1 + comboTaxRate) : parseFloat(i.price);
      return { variant_id: i.variant_id, price: comboPrice.toFixed(2) };
    });

    //determine what to do with lineItem based off quantity wanted
    if (isNumeric(lineItemState.inputPrice)) {
      const lineItemId = lineItemState?.lineItem?.id;
      const lineItemParentId = lineItemState?.lineItem?.parent_id;
      //Get the key values of the line item that can be edited
      const lineItemPickedKeys = (({ quantity, price, note }) => ({
        quantity,
        price,
        note,
      }))(lineItemState?.lineItem);

      const params = {
        price: price,
        comboPrices: comboPrices,
        quantity: quantity,
        note: isEmpty(lineItemState.inputNote) ? null : lineItemState.inputNote,
        tax_exemption_applied: lineItemState.taxExemptionApplied,
      };

      //Update line item if a change was made
      if (!isEqual(lineItemPickedKeys, params)) {
        void addLineItemUpdating(lineItemParentId ?? lineItemId);
        // Keep modal open when updating as part of applying a discount
        if (!discountApplied) {
          void resetInputState();
        }
        const res = await PutLineItemToCart({ id: lineItemId, ...params }, true);
        void removeLineItemUpdating(lineItemParentId ?? lineItemId);
        if (res && res.status !== StatusCode.OK) {
          dispatch(showError(t("secure.facility.tee_sheet.tee_sheet_booking_details.019")));
          return;
        } else if (res && res.status === StatusCode.OK) {
          //Don't set cart in state if discount is being applied
          if (discountApplied === false) {
            if (dataOrigin === "redux") {
              void dispatch(loadTableCart({ tableCart: res?.data as Record<string, any> }, false));
              modalActions?.updateTabCart(res?.data);
            } else {
              modalActions?.onOk(res?.data);
              modalActions?.updateTabCart(res?.data);
            }
          }
        }
      }
    } else {
      dispatch(showError(t("secure.facility.tee_sheet.tee_sheet_booking_details.018")));
    }
  }

  function handleNoteCHange(e: React.ChangeEvent<HTMLInputElement>) {
    setDiscountModal(prevState => ({ ...prevState, noteDiscount: e.target.value }));
  }

  // Remove the selected lineItem
  async function handleRemoveLineItem() {
    if (!canRemoveCartLineItem(permissions, lineItemState?.lineItem)) {
      dispatch(showError(t("You do not have permission to remove items from the cart.")));
      return;
    }

    const lineItem = { ...lineItemState?.lineItem };
    const updateTeeSheetParticipant =
      dataOrigin === "state" &&
      Boolean(teeSheetStore?.selectedTeeTime?.slots?.length) &&
      (lineItem?.product?.type === "Green Fee" || lineItem?.product?.type === "Power Cart");
    const feeType = lineItem?.product?.type === "Green Fee" ? "green_fee" : "power_cart_fee";
    let updatedParticipant: {
      booking_participant_id: number;
      green_fee: boolean;
      power_cart_fee: boolean;
      green_fee_loading: boolean;
      power_cart_fee_loading: boolean;
    } = undefined;

    void addLineItemUpdating(lineItem?.parent_id ?? lineItem?.id, "delete");
    //Reset the modal
    void resetInputState();
    if (updateTeeSheetParticipant) {
      updatedParticipant = setLoadingParticipant(lineItem);
    }
    const deleteRes = await DeleteLineItemToCart({ id: lineItem?.id }, false);
    void removeLineItemUpdating(lineItem?.parent_id ?? lineItem?.id);
    if (deleteRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.tee_sheet.tee_sheet_booking_details.017")));
      if (updateTeeSheetParticipant && updatedParticipant && participantPaidFees?.current) {
        const participant = participantPaidFees.current.find(
          participant => participant?.booking_participant_id === updatedParticipant?.booking_participant_id,
        );

        if (participant) {
          updateParticipantPaidFees({ ...participant, [`${feeType}_loading`]: false });
        }
      }
      return;
    }

    dispatch(showSuccess(t("secure.facility.tee_sheet.tee_sheet_booking_details.246")));
    //Redux cart (Register)
    if (dataOrigin === "redux") {
      void dispatch(loadTableCart({ tableCart: deleteRes?.data }, false));
    } else {
      if (updatedParticipant && updateTeeSheetParticipant && participantPaidFees?.current) {
        //Variant removed from cart, set participant fee value to false
        const participant = [...participantPaidFees.current].find(
          participant => participant?.booking_participant_id === updatedParticipant?.booking_participant_id,
        );

        if (participant) {
          updateParticipantPaidFees({ ...participant, [`${feeType}`]: false, [`${feeType}_loading`]: false });
        }
      }
      //State cart (Tee Sheet Details)
      modalActions?.onOk(deleteRes?.data as ICart);
    }
  }

  //Display loader for booking participants with matching green fee / power cart fee
  function setLoadingParticipant(lineItem: ICartLineItem) {
    const productType = lineItem?.product?.type === "Green Fee" ? "green_fee_variant_id" : "power_cart_variant_id";
    const feeType = lineItem?.product?.type === "Green Fee" ? "green_fee" : "power_cart_fee";
    let updatedParticipant: {
      booking_participant_id: number;
      green_fee: boolean;
      power_cart_fee: boolean;
      green_fee_loading: boolean;
      power_cart_fee_loading: boolean;
    } = undefined;
    if (!teeSheetStore?.selectedTeeTime?.slots?.length) {
      return updatedParticipant;
    }
    [...teeSheetStore.selectedTeeTime.slots].reverse().every(slot => {
      if (slot?.booking_participant && slot?.booking_participant?.[productType] === lineItem?.variant_id) {
        return [...participantPaidFees.current].reverse().every(participant => {
          if (
            participant?.booking_participant_id === slot?.booking_participant?.id &&
            participant?.[`${feeType}`] === true &&
            participant?.[`${feeType}_loading`] === false
          ) {
            updatedParticipant = { ...participant, [`${feeType}_loading`]: true };
            updateParticipantPaidFees(updatedParticipant);
            return false;
          }
          return true;
        });
      }
      return true;
    });
    return updatedParticipant;
  }

  function getModifierLineItemIds() {
    const cartLineItems = [...cart.line_items];
    const parentLineItemId = lineItemState?.lineItem?.id;
    return cartLineItems?.filter(lineItem => lineItem?.parent_id === parentLineItemId)?.map(lineItem => lineItem?.id);
  }

  // Apply discount to line item
  async function handleLineItemDiscountApply(customAmount?: string, typeFixed?: boolean) {
    const updatingLineItemId = lineItemState?.lineItem?.id;
    const lineItemParentId = lineItemState?.lineItem?.parent_id;
    const type = typeFixed !== undefined ? (typeFixed ? "fixed_amount" : "percent") : undefined;
    const modifierLineItemIds = getModifierLineItemIds();
    const selectedDiscount = discountModal.selectedDiscount;
    //Update line item quantity, price and/or note first
    // await handleUpdateLineItem(true);

    void addLineItemUpdating(lineItemParentId ?? updatingLineItemId);

    const res = await PostDiscountLine(
      {
        discount_id: discountModal.selectedDiscountId,
        cart_id: lineItemState.lineItem.cart_id,
        cart_line_item_id: updatingLineItemId,
        value: customAmount ? Number(customAmount) : undefined,
        value_type: type,
        notes: discountModal.noteDiscount,
        modifier_line_item_ids:
          modifierLineItemIds?.length > 0 &&
          (type == "percent" || (!type && selectedDiscount?.value_type === "percent"))
            ? modifierLineItemIds
            : undefined,
      },
      true,
    );
    void removeLineItemUpdating(lineItemParentId ?? updatingLineItemId);

    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.tee_sheet.tee_sheet_booking_details.020")));
      return;
    }

    setAppliedDiscountLabel(
      selectedDiscount?.custom
        ? t("Custom discount applied")
        : t("{{title}} applied", { title: selectedDiscount?.title || t("Discount") }),
    );
    closeDiscountModal(true);

    // allow parent to handle the change
    if (dataOrigin === "state") {
      void modalActions?.onOk(res?.data);
    } else if (dataOrigin === "redux") {
      if (res?.data) {
        dispatch(loadTableCart({ tableCart: res?.data }, false));
      } else {
        dispatch(loadCart({ id: lineItemState.lineItem.cart_id }, true));
      }
    }

    dispatch(showSuccess(t("secure.facility.tee_sheet.tee_sheet_booking_details.171")));
  }

  function openModifiersRegisterNew(lineItem: ICartLineItem) {
    setLineItemState(prevState => ({ ...prevState, isModalVisible: false }));
    openModifiersSheet(lineItem);
  }

  function handleOpenDynamicComboSheet(lineItem: ICartLineItem) {
    setLineItemState(prevState => ({ ...prevState, isModalVisible: false }));
    openDynamicComboSheet(lineItem);
  }

  function getComboVariantQuantity(comboVariant: TComboVariants): number {
    const selection = lineItemState.lineItem?.combo_selections?.find(
      comboSelection => comboSelection.variant_id === comboVariant.id,
    );
    if (selection?.quantity) {
      return Number(selection.quantity);
    }
    if (comboVariant.combo_pivot?.quantity) {
      return Number(comboVariant.combo_pivot.quantity);
    }
    return 1;
  }

  const currencySymbol = getCurrencySymbol(cart?.currency) ?? "$";
  const formattedTotalPrice = isNumeric(lineItemState.inputPrice)
    ? Number(lineItemState.inputPrice).toFixed(2)
    : "0.00";
  const canEditComboPrices =
    !!lineItemState?.lineItem?.product?.dynamic_price ||
    (permissions?.orders_edit_line_item_price && !lineItemState?.lineItem?.product?.price_locked);
  const showTaxToggle = (lineItemState.lineItem?.tax_lines?.length ?? 0) > 0;

  return (
    <>
      <Sheet
        closable
        size="small"
        open={lineItemState.isModalVisible}
        title={t("Update item")}
        supportText={
          isDynamicCombo || isRegularCombo
            ? t("Manage this combo's quantity, pricing and products before checkout.")
            : t("Manage the quantity, pricing and products before checkout.")
        }
        onCancel={() => resetInputState()}
        footer={null}
        backDropCancel={false}
        stacked
      >
        <div
          className={
            isRegularCombo && !hasModifiers
              ? "update-line-item-modal update-line-item-modal--regular-combo"
              : "update-line-item-modal"
          }
        >
          <div className="update-line-item-modal-scroll">
            <div className="update-line-item-modal-top">
              <div className="flex flex-col gap-1 mb-4">
                <span className="text-md text-semibold text-gray-900">
                  {lineItemState.lineItem?.preferred_title
                    ? lineItemState?.lineItem?.preferred_title
                    : lineItemState?.lineItem?.product_title}
                </span>
                {lineItemState.lineItem?.preferred_title !== lineItemState.lineItem?.variant_title &&
                  lineItemState?.lineItem?.product_title !== lineItemState.lineItem?.variant_title && (
                    <span className="text-sm text-gray-600">{lineItemState.lineItem?.variant_title}</span>
                  )}
              </div>

              <div className="update-line-item-modal-fields">
                <div className="update-line-item-modal-frame-grey">
                  <div className="update-line-item-modal-frame-grey-field">
                    <Input
                      className="update-line-item-modal-field-input"
                      label={t("Quantity")}
                      id="inputQuantity"
                      type="number"
                      min={minimumQuantity}
                      value={lineItemState.inputQuantity || ""}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setLineItemState({ ...lineItemState, [e.target.id]: e.target.value })
                      }
                      onBlur={() => {
                        if (isEmpty(lineItemState.inputQuantity)) {
                          setLineItemState({
                            ...lineItemState,
                            inputQuantity: String(originalQuantity),
                          });
                          return;
                        }

                        const parsedQuantity = parseInt(lineItemState.inputQuantity);
                        if (!canSetCartLineItemQuantity(permissions, parsedQuantity, lineItemState?.lineItem)) {
                          setLineItemState({
                            ...lineItemState,
                            inputQuantity: String(minimumQuantity),
                          });
                        }
                      }}
                      error={!isNumeric(lineItemState.inputQuantity)}
                    />
                  </div>
                  <div className="update-line-item-modal-frame-grey-divider" />
                  <div className="update-line-item-modal-frame-grey-field">
                    {isRegularCombo ? (
                      <div className="update-line-item-modal-auto-price">
                        <div className="update-line-item-modal-auto-price-header">
                          <span className="text-sm text-medium text-gray-700">{t("Total price")}</span>
                          <Badge type="grayblue" size="small" outline>
                            {t("Auto-calculated")}
                          </Badge>
                        </div>
                        <p className="update-line-item-modal-auto-price-value">
                          {currencySymbol}
                          {formattedTotalPrice}
                        </p>
                      </div>
                    ) : (
                      <Input
                        className="update-line-item-modal-field-input"
                        prefix={currencySymbol}
                        label={lineItemState?.lineItem?.product?.type === "Gift Card" ? t("Amount") : t("Total price")}
                        placeholder={
                          lineItemState?.lineItem?.product?.type === "Gift Card"
                            ? t("Enter Amount...")
                            : t("Enter price...")
                        }
                        id="inputPrice"
                        type="number"
                        value={lineItemState.inputPrice || ""}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setLineItemState({ ...lineItemState, inputPrice: e.target.value })
                        }
                        disabled={
                          !lineItemState?.lineItem?.product?.dynamic_price &&
                          (!permissions?.orders_edit_line_item_price || lineItemState?.lineItem?.product?.price_locked)
                        }
                        error={!isNumeric(lineItemState.inputPrice)}
                        autoFocus={true}
                      />
                    )}
                  </div>
                </div>

                {!isRegularCombo && showTaxToggle ? (
                  <div className="update-line-item-modal-tax-toggle">
                    <Toggle
                      labelRight={t("Show price after applying tax")}
                      checked={isPriceAfterTax}
                      onChange={handlePriceAfterTaxToggle}
                    />
                  </div>
                ) : null}
              </div>

              {isRegularCombo ? (
                <>
                  <div className="update-line-item-modal-divider" />
                  <div className="update-line-item-modal-price-per-product-header">
                    <p className="text-md text-semibold text-gray-900">{t("Price per product")}</p>
                    {showTaxToggle ? (
                      <div className="update-line-item-modal-tax-toggle">
                        <Toggle
                          labelRight={t("Show price after applying tax")}
                          checked={isPriceAfterTax}
                          onChange={handlePriceAfterTaxToggle}
                        />
                      </div>
                    ) : null}
                  </div>
                </>
              ) : null}
            </div>

            {isRegularCombo ? (
              <div className="update-line-item-modal-price-per-product-grid">
                {variant?.combination_variants?.map((comboVariant, index) => {
                  const comboPriceIndex = lineItemState.inputComboPrices?.findIndex(
                    priceItem => priceItem.variant_id === comboVariant.id,
                  );
                  const comboPriceValue =
                    comboPriceIndex >= 0 ? lineItemState.inputComboPrices[comboPriceIndex]?.price ?? "" : "";

                  return (
                    <div key={comboVariant.id} className="update-line-item-modal-price-per-product-card">
                      <div className="update-line-item-modal-price-per-product-card-header">
                        <span className="update-line-item-modal-price-per-product-card-title">
                          {comboVariant.title}
                        </span>
                        <span className="update-line-item-modal-price-per-product-card-qty">
                          {getComboVariantQuantity(comboVariant)}x
                        </span>
                      </div>
                      <Input
                        prefix={currencySymbol}
                        placeholder={t("Enter price...")}
                        id={`inputComboPrice-${comboVariant.id}`}
                        type="number"
                        value={comboPriceValue}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          const inputComboPrices = [...(lineItemState.inputComboPrices ?? [])];
                          if (comboPriceIndex >= 0) {
                            inputComboPrices[comboPriceIndex] = {
                              variant_id: comboVariant.id,
                              price: e.target.value,
                            };
                          } else {
                            inputComboPrices.push({ variant_id: comboVariant.id, price: e.target.value });
                          }
                          const priceTotal = inputComboPrices
                            .reduce((sum, priceItem) => sum + (parseFloat(priceItem.price) || 0), 0)
                            .toFixed(2);
                          setLineItemState({
                            ...lineItemState,
                            inputComboPrices,
                            inputPrice: priceTotal,
                          });
                        }}
                        disabled={!canEditComboPrices}
                        error={!!comboPriceValue && !isNumeric(comboPriceValue)}
                        autoFocus={index === 0}
                      />
                    </div>
                  );
                })}
              </div>
            ) : null}

            {isDynamicCombo ? (
              <>
                <div className="update-line-item-modal-divider" />
                <div className="update-line-item-modal-included-products">
                  <div className="update-line-item-modal-included-products-header">
                    <p className="text-md text-semibold text-gray-900">
                      {t("Included products")}{" "}
                      <span className="text-regular text-gray-500">({dynamicComboSelections.length})</span>
                    </p>
                    <div className="update-line-item-modal-included-products-actions">
                      <Button
                        type="secondary"
                        size="small"
                        onClick={() => handleOpenDynamicComboSheet(lineItemState?.lineItem)}
                      >
                        {t("Edit")}
                      </Button>
                      <button
                        type="button"
                        className="update-line-item-modal-included-products-collapse"
                        onClick={() => setIsIncludedProductsExpanded(prev => !prev)}
                        aria-label={isIncludedProductsExpanded ? t("Collapse") : t("Expand")}
                      >
                        <FontAwesomeIcon icon={isIncludedProductsExpanded ? faChevronUp : faChevronDown} />
                      </button>
                    </div>
                  </div>

                  {isIncludedProductsExpanded ? (
                    <>
                      <div className="update-line-item-modal-included-products-divider" />
                      <div className="update-line-item-modal-included-products-tags">
                        {dynamicComboSelections.map(selection => (
                          <div key={selection.id} className="update-line-item-modal-included-products-tag">
                            <span>{selection.variant?.title}</span>
                            <span className="update-line-item-modal-included-products-tag-count">
                              {selection.quantity}
                            </span>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : null}
                </div>
              </>
            ) : null}

            {hasModifiers ? (
              <>
                <div className="update-line-item-modal-divider" />
                <div className="update-line-item-modal-included-products">
                  <div className="update-line-item-modal-included-products-header">
                    <p className="text-md text-semibold text-gray-900">
                      {t("Modifiers")} <span className="text-regular text-gray-500">({modifierLineItems.length})</span>
                    </p>
                    <div className="update-line-item-modal-included-products-actions">
                      <Button
                        type="secondary"
                        size="small"
                        onClick={() => openModifiersRegisterNew(lineItemState?.lineItem)}
                      >
                        {t("Edit")}
                      </Button>
                      <button
                        type="button"
                        className="update-line-item-modal-included-products-collapse"
                        onClick={() => setIsModifiersExpanded(prev => !prev)}
                        aria-label={isModifiersExpanded ? t("Collapse") : t("Expand")}
                      >
                        <FontAwesomeIcon icon={isModifiersExpanded ? faChevronUp : faChevronDown} />
                      </button>
                    </div>
                  </div>

                  {isModifiersExpanded ? (
                    <>
                      <div className="update-line-item-modal-included-products-divider" />
                      <div className="update-line-item-modal-included-products-tags">
                        {modifierLineItems.map(modifierLineItem => (
                          <div key={modifierLineItem.id} className="update-line-item-modal-included-products-tag">
                            <span>
                              {modifierLineItem.preferred_title ||
                                modifierLineItem.product_title ||
                                modifierLineItem.variant_title}
                            </span>
                            <span className="update-line-item-modal-included-products-tag-count">
                              {modifierLineItem.quantity}
                            </span>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : null}
                </div>
              </>
            ) : null}

            <div className="update-line-item-modal-bottom">
              <div className="update-line-item-modal-divider" />

              <button
                type="button"
                className="update-line-item-modal-action-row"
                onClick={() => openDiscountModal()}
                disabled={!permissions?.orders_apply_discount}
              >
                <div className="update-line-item-modal-action-row-left">
                  <span className="text-md text-semibold text-gray-900">{t("Apply discount")}</span>
                  {discountBadgeText ? (
                    <Badge type="success" size="small" outline>
                      {discountBadgeText}
                    </Badge>
                  ) : null}
                </div>
                <FontAwesomeIcon icon={faChevronRight} className="text-gray-600" />
              </button>

              <div className="update-line-item-modal-divider" />

              {isNoteVisible || !isEmpty(lineItemState.inputNote) ? (
                <div className="mb-4">
                  <Input
                    label={t("Note")}
                    placeholder={t("Note")}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setLineItemState({ ...lineItemState, [e.target.id]: e.target.value })
                    }
                    id="inputNote"
                    value={lineItemState.inputNote}
                  />
                </div>
              ) : (
                <Button type="text" onClick={() => setIsNoteVisible(true)} className="update-line-item-modal-add-note">
                  <FontAwesomeIcon icon={faPlus} />
                  {t("Add note")}
                </Button>
              )}

              {lineItemState.lineItem?.product?.tax_exempt && cart?.customer?.tax_exempt ? (
                <div className="mb-4">
                  <Toggle
                    label={t("Tax Exempt")}
                    checked={lineItemState.taxExemptionApplied}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setLineItemState({ ...lineItemState, taxExemptionApplied: e.target.checked })
                    }
                  />
                </div>
              ) : null}
            </div>
          </div>

          <div className="update-line-item-modal-footer">
            <Button
              type="text"
              className="update-line-item-modal-error"
              onClick={() => void handleRemoveLineItem()}
              disabled={!canRemoveCartLineItem(permissions, lineItemState?.lineItem)}
            >
              {t("Remove from cart")}
            </Button>
            <div className="update-line-item-modal-footer-actions">
              <Button type="secondary" onClick={() => resetInputState()}>
                {t("Cancel")}
              </Button>
              <Button
                type="primary"
                onClick={() => handleUpdateLineItem(false)}
                disabled={
                  !isNumeric(lineItemState.inputQuantity) ||
                  !isNumeric(lineItemState.inputPrice) ||
                  !canSetCartLineItemQuantity(
                    permissions,
                    parseInt(lineItemState.inputQuantity),
                    lineItemState?.lineItem,
                  )
                }
              >
                {t("Save changes")}
              </Button>
            </div>
          </div>
        </div>
      </Sheet>

      {discounts && (
        <DiscountLineItemModal
          title={t("Line item discount")}
          supportText={t("Applied to {{name}} • {{price}}", {
            name: lineItemState.lineItem?.preferred_title || lineItemState.lineItem?.product_title || "",
            price: Number(lineItemState.inputPrice || lineItemState.lineItem?.total_price || 0).toFixed(2),
          })}
          open={discountModal.isOpen}
          onCancel={() => closeDiscountModal(true)}
          onOk={(customAmount, typeFixed) => void handleLineItemDiscountApply(customAmount, typeFixed)}
          noteDiscount={discountModal.noteDiscount}
          changeNoteState={handleNoteCHange}
          onChange={(id: number, discount: IDiscount) =>
            setDiscountModal({ ...discountModal, selectedDiscountId: id, selectedDiscount: discount })
          }
          okDisabled={!discountModal.selectedDiscountId || discountModal.selectedDiscountId < 0}
          discounts={discounts}
          selectedDiscount={discountModal.selectedDiscount}
        />
      )}
    </>
  );
}
